import React, { useCallback, useState } from 'react';

import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDesktop } from '@rtkwlf/pro-solid-svg-icons/faDesktop';
import { faGlobeAmericas } from '@rtkwlf/pro-solid-svg-icons/faGlobeAmericas';
import { faMapMarkedAlt } from '@rtkwlf/pro-solid-svg-icons/faMapMarkedAlt';
import { faRepeat } from '@rtkwlf/pro-solid-svg-icons/faRepeat';
import { faSearch } from '@rtkwlf/pro-solid-svg-icons/faSearch';
import { faSensor } from '@rtkwlf/pro-solid-svg-icons/faSensor';
import { faSignInAlt } from '@rtkwlf/pro-solid-svg-icons/faSignInAlt';
import { faSignOutAlt } from '@rtkwlf/pro-solid-svg-icons/faSignOutAlt';
import type { DataFlow } from '@rtkwlf/rendall';
import { stringify } from 'query-string';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';
import { encodeQueryParams } from 'serialize-query-params';
import { StringParam, useQueryParams } from 'use-query-params';

import {
  Button,
  ListItemIcon,
  ListItemText,
  Menu,
  Paper,
} from '@packages/fenrir/components';
import { MenuItem } from '@packages/fenrir/components/menu-item';
import { CommaArrayParam } from '@packages/utils/comma-array-param/comma-array-param';
import { DayJsParam } from '@packages/utils/dayjs.utils';

import { DATA_DIRECTIONS } from '../../flow-data-filters/hooks/use-direction-options/use-direction-options';
import type { FlowDataType } from '../../flow-data-type';
import { isFlowDataType } from '../../flow-data-type';
import { useFlowDataTypeParam } from '../../hooks/use-flow-data-type-param/use-flow-data-type-param';

type Key =
  | 'device'
  | 'protocol'
  | 'location'
  | 'site'
  | 'sensor'
  | DATA_DIRECTIONS.INBOUND
  | DATA_DIRECTIONS.OUTBOUND;

const options: { icon: IconDefinition; key: Key }[] = [
  { icon: faDesktop, key: 'device' },
  { icon: faRepeat, key: 'protocol' },
  { icon: faGlobeAmericas, key: 'location' },
  { icon: faMapMarkedAlt, key: 'site' },
  { icon: faSensor, key: 'sensor' },
  { icon: faSignInAlt, key: DATA_DIRECTIONS.INBOUND },
  { icon: faSignOutAlt, key: DATA_DIRECTIONS.OUTBOUND },
];

const DIRECTIONS = [DATA_DIRECTIONS.INBOUND, DATA_DIRECTIONS.OUTBOUND];

type PivotProps = { data: DataFlow };

const queryParamConfigMap = {
  device: CommaArrayParam,
  protocol: CommaArrayParam,
  location: CommaArrayParam,
  site: CommaArrayParam,
  sensor: CommaArrayParam,
  direction: StringParam,
  start: DayJsParam,
  end: DayJsParam,
};

function generatePivotQueryParams(
  key: Key,
  flowDataType: FlowDataType,
  data: DataFlow,
) {
  const isDirectionChange =
    key === DATA_DIRECTIONS.INBOUND || key === DATA_DIRECTIONS.OUTBOUND;
  if (isDirectionChange) {
    return {
      direction: key,
    };
  }

  if (flowDataType === 'location') {
    return { location: data.code ? [data.code] : [] };
  }

  return { [flowDataType]: [data.name] };
}

export const Pivot = ({ data }: PivotProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { t } = useTranslation('flow');
  const navigate = useNavigate();
  const flowDataType = useFlowDataTypeParam();

  const [queryParams, setQueryParams] = useQueryParams(queryParamConfigMap);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const isOptionDisabled = useCallback(
    (key: Key): boolean => {
      if (isFlowDataType(key)) {
        const isCurrentTab = flowDataType === key;
        const hasFilterAppliedForKey = Boolean(queryParams[key]?.length);
        return hasFilterAppliedForKey || isCurrentTab;
      }

      if (DIRECTIONS.includes(key)) {
        const isAlreadyFilteringByDirection = queryParams.direction === key;
        return isAlreadyFilteringByDirection;
      }

      return false;
    },
    [flowDataType, queryParams],
  );

  const applyPivot = useCallback(
    (key: Key) => () => {
      const newQueryParams = {
        ...queryParams,
        ...generatePivotQueryParams(key, flowDataType, data),
      };

      const isSwitchingTabs = isFlowDataType(key);
      if (isSwitchingTabs) {
        navigate({
          pathname: generatePath(`/data-explorer/flow/${key}`),
          search: stringify(
            encodeQueryParams(queryParamConfigMap, newQueryParams),
          ),
        });
      } else {
        setQueryParams(newQueryParams);
      }

      handleClose();
    },
    [data, flowDataType, navigate, queryParams, setQueryParams],
  );

  return (
    <div>
      <Button
        aria-label={t('pivot')}
        onClick={handleClick}
        aria-haspopup="true"
        endIcon={<FontAwesomeIcon icon={faSearch} size="sm" />}
      >
        {t('pivot')}
      </Button>
      <Paper>
        <Menu
          id="long-menu"
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handleClose}
        >
          {options.map(({ icon, key }) => (
            <MenuItem
              key={key}
              aria-label={t(`pivotAction.${key}`)}
              disabled={isOptionDisabled(key)}
              onClick={applyPivot(key)}
            >
              <ListItemIcon>
                <FontAwesomeIcon icon={icon} size="sm" />
              </ListItemIcon>
              <ListItemText>{t(`pivotAction.${key}`)}</ListItemText>
            </MenuItem>
          ))}
        </Menu>
      </Paper>
    </div>
  );
};
